import React, { useState, useEffect } from 'react';
import {
  Save,
  Upload,
  Palette,
  Image as ImageIcon,
  Globe,
  FileText,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Settings,
  RotateCcw,
  Sun,
  Moon,
} from 'lucide-react';
import { MainLayout } from '../../components/layout';
import Button from '../../components/ui/Button';
import { configurationService } from '../../services/configuration.service';
import { useBranding } from '../../contexts/BrandingContext';

const ConfigurationPage: React.FC = () => {
  const { branding, refreshBranding } = useBranding();
  
  const [config, setConfig] = useState({
    brand_name: 'Vision 24',
    logo_url: '',
    favicon_url: '',
    primary_color: '#D32F2F',
    secondary_color: '#EF5350',
    login_background: '',
    login_title: 'Welcome to Vision 24',
    login_subtitle: 'Secure Camera Management Platform',
    footer_text: '© 2026 All rights reserved',
    theme_mode: 'dark',
    event_retention_days: 30,
  });

  const [logoPreview, setLogoPreview] = useState<string>('');
  const [faviconPreview, setFaviconPreview] = useState<string>('');
  const [loginBgPreview, setLoginBgPreview] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setIsLoading(true);
      const data = await configurationService.getConfiguration();
      
      if (data) {
        setConfig({
          brand_name: data.brand_name || 'Vision 24',
          logo_url: data.logo_url || '',
          favicon_url: data.favicon_url || '',
          primary_color: data.primary_color || '#D32F2F',
          secondary_color: data.secondary_color || '#EF5350',
          login_background: data.login_background || '',
          login_title: data.login_title || 'Welcome to Vision 24',
          login_subtitle: data.login_subtitle || 'Secure Camera Management Platform',
          footer_text: data.footer_text || '© 2026 All rights reserved',
          theme_mode: data.theme_mode || 'dark',
          event_retention_days: data.event_retention_days || 30,
        });

        setLogoPreview(data.logo_url || '');
        setFaviconPreview(data.favicon_url || '');
        setLoginBgPreview(data.login_background || '');
      }
    } catch (error) {
      console.error('Failed to load configuration:', error);
      showMessage('error', 'Failed to load configuration. Please ensure database migration has been run.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof config, value: string) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'logo' | 'favicon' | 'loginBg'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please select a valid image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showMessage('error', 'Image size must be less than 2MB');
      return;
    }

    try {
      const dataUrl = await configurationService.uploadImage(
        type === 'logo' ? 'logo' : type === 'favicon' ? 'favicon' : 'login_bg',
        file
      );

      if (type === 'logo') {
        setLogoPreview(dataUrl);
        setConfig(prev => ({ ...prev, logo_url: dataUrl }));
      } else if (type === 'favicon') {
        setFaviconPreview(dataUrl);
        setConfig(prev => ({ ...prev, favicon_url: dataUrl }));
      } else {
        setLoginBgPreview(dataUrl);
        setConfig(prev => ({ ...prev, login_background: dataUrl }));
      }

      showMessage('success', 'Image uploaded successfully');
    } catch (error) {
      console.error('Failed to upload image:', error);
      showMessage('error', 'Failed to upload image');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);
    
    try {
      console.log('💾 Saving configuration:', config);
      const result = await configurationService.updateConfiguration(config);
      console.log('✅ Save response:', result);
      
      // Clear localStorage cache to force fresh load
      localStorage.removeItem('companyBranding');
      
      await refreshBranding();
      console.log('✅ Branding refreshed');
      showMessage('success', 'Configuration saved successfully!');
    } catch (error) {
      console.error('❌ Error saving configuration:', error);
      showMessage('error', 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-accent-primary" size={32} />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-stack">
        <div className="page-header">
          <div>
            <h1 className="page-title">Configuration</h1>
            <p className="page-description">
              Customize your company branding and settings
            </p>
            {branding && branding.brand_name && (
              <p className="text-sm text-text-muted mt-1">
                Current Brand: <span className="font-medium text-text-primary">{branding.brand_name}</span>
              </p>
            )}
          </div>
          <Button onClick={handleSave} disabled={isSaving} className="flex items-center gap-2">
            {isSaving ? <RefreshCw className="animate-spin" size={18} /> : <Save size={18} />}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        {message && (
          <div className={`flex items-center gap-3 p-4 rounded-lg border-2 ${
              message.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-600' : 'bg-red-500/10 border-red-500/20 text-red-600'
            }`}>
            {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            <span className="font-medium">{message.text}</span>
          </div>
        )}

        {/* General Settings - Full Width */}
        <div className="bg-surface-primary rounded-card p-6 border-2 border-accent-primary/20 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-lg flex items-center justify-center">
              <Settings size={20} className="text-white" />
            </div>
            <h2 className="text-xl font-semibold text-text-primary">General Settings</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Brand Display Name
              <span className="text-xs text-text-muted ml-2">(Sidebar, login page, etc.)</span>
            </label>
            <input
              type="text"
              value={config.brand_name}
              onChange={(e) => handleInputChange('brand_name', e.target.value)}
              placeholder="Enter brand display name"
              className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
            />
            <p className="text-xs text-text-muted mt-1">⚠️ This is for branding only and does not affect your company profile information</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Logo & Images */}
          <div className="bg-surface-primary rounded-card p-6 border-2 border-accent-primary/20 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-lg flex items-center justify-center">
                <ImageIcon size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Logo & Images</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Company Logo</label>
                <div className="flex items-center gap-4">
                  {logoPreview && (
                    <div className="w-20 h-20 border-2 border-surface-secondary rounded-lg overflow-hidden bg-white">
                      <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                    </div>
                  )}
                  <label className="flex-1">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'logo')} />
                    <div className="flex items-center gap-2 px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg cursor-pointer hover:bg-surface-hover transition-colors">
                      <Upload size={16} />
                      <span className="text-sm text-text-primary">Upload Logo</span>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-text-muted mt-1">Recommended: 200x200px, PNG or SVG</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Favicon</label>
                <div className="flex items-center gap-4">
                  {faviconPreview && (
                    <div className="w-12 h-12 border-2 border-surface-secondary rounded-lg overflow-hidden bg-white">
                      <img src={faviconPreview} alt="Favicon" className="w-full h-full object-contain" />
                    </div>
                  )}
                  <label className="flex-1">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'favicon')} />
                    <div className="flex items-center gap-2 px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg cursor-pointer hover:bg-surface-hover transition-colors">
                      <Upload size={16} />
                      <span className="text-sm text-text-primary">Upload Favicon</span>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-text-muted mt-1">Recommended: 32x32px, PNG or ICO</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Login Background</label>
                <div className="space-y-2">
                  {loginBgPreview && (
                    <div className="w-full h-32 border-2 border-surface-secondary rounded-lg overflow-hidden">
                      <img src={loginBgPreview} alt="Login BG" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <label>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'loginBg')} />
                    <div className="flex items-center gap-2 px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg cursor-pointer hover:bg-surface-hover transition-colors">
                      <Upload size={16} />
                      <span className="text-sm text-text-primary">Upload Background</span>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-text-muted mt-1">Recommended: 1920x1080px</p>
              </div>
            </div>
          </div>

          {/* Brand Colors */}
          <div className="bg-surface-primary rounded-card p-6 border-2 border-accent-primary/20 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-lg flex items-center justify-center">
                  <Palette size={20} className="text-white" />
                </div>
                <h2 className="text-xl font-semibold text-text-primary">Brand Colors</h2>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setConfig(prev => ({
                    ...prev,
                    primary_color: '#D32F2F',
                    secondary_color: '#EF5350'
                  }));
                }}
                className="flex items-center gap-2"
              >
                <RotateCcw size={16} />
                Reset to Default
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={config.primary_color} onChange={(e) => handleInputChange('primary_color', e.target.value)} className="w-16 h-10 rounded-lg cursor-pointer border-2 border-surface-secondary" />
                  <input type="text" value={config.primary_color} onChange={(e) => handleInputChange('primary_color', e.target.value)} className="flex-1 px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={config.secondary_color} onChange={(e) => handleInputChange('secondary_color', e.target.value)} className="w-16 h-10 rounded-lg cursor-pointer border-2 border-surface-secondary" />
                  <input type="text" value={config.secondary_color} onChange={(e) => handleInputChange('secondary_color', e.target.value)} className="flex-1 px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary" />
                </div>
              </div>

              <div className="p-4 bg-surface-secondary rounded-lg">
                <p className="text-sm text-text-secondary mb-2">Color Preview:</p>
                <div className="flex gap-2">
                  <div className="flex-1 h-12 rounded-lg flex items-center justify-center text-white font-medium" style={{ backgroundColor: config.primary_color }}>Primary</div>
                  <div className="flex-1 h-12 rounded-lg flex items-center justify-center text-white font-medium" style={{ backgroundColor: config.secondary_color }}>Secondary</div>
                </div>
              </div>
            </div>
          </div>

          {/* Theme Mode */}
          <div className="bg-surface-primary rounded-card p-6 border-2 border-accent-primary/20 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-lg flex items-center justify-center">
                {config.theme_mode === 'light' ? (
                  <Sun size={20} className="text-white" />
                ) : (
                  <Moon size={20} className="text-white" />
                )}
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Theme Mode</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">Select Theme</label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => handleInputChange('theme_mode', 'dark')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      config.theme_mode === 'dark'
                        ? 'border-accent-primary bg-accent-primary/10'
                        : 'border-surface-secondary bg-surface-secondary hover:border-accent-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        config.theme_mode === 'dark' ? 'bg-accent-primary' : 'bg-surface-hover'
                      }`}>
                        <Moon size={20} className="text-white" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-text-primary">Dark Mode</div>
                        <div className="text-xs text-text-muted">Default theme</div>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleInputChange('theme_mode', 'light')}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      config.theme_mode === 'light'
                        ? 'border-accent-primary bg-accent-primary/10'
                        : 'border-surface-secondary bg-surface-secondary hover:border-accent-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        config.theme_mode === 'light' ? 'bg-accent-primary' : 'bg-surface-hover'
                      }`}>
                        <Sun size={20} className="text-white" />
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-text-primary">Light Mode</div>
                        <div className="text-xs text-text-muted">Coming soon</div>
                      </div>
                    </div>
                  </button>
                </div>
                <p className="text-xs text-text-muted mt-3">
                  ⚠️ Light mode is experimental and may not be fully styled yet.
                </p>
              </div>
            </div>
          </div>

          {/* Login Page */}
          <div className="bg-surface-primary rounded-card p-6 border-2 border-accent-primary/20 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-lg flex items-center justify-center">
                <Globe size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Login Page</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Title</label>
                <input type="text" value={config.login_title} onChange={(e) => handleInputChange('login_title', e.target.value)} className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Subtitle</label>
                <input type="text" value={config.login_subtitle} onChange={(e) => handleInputChange('login_subtitle', e.target.value)} className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-surface-primary rounded-card p-6 border-2 border-accent-primary/20 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-lg flex items-center justify-center">
                <FileText size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Footer</h2>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Footer Text</label>
              <input type="text" value={config.footer_text} onChange={(e) => handleInputChange('footer_text', e.target.value)} className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary" />
            </div>
          </div>

          {/* Event Retention */}
          <div className="bg-surface-primary rounded-card p-6 border-2 border-accent-primary/20 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-lg flex items-center justify-center">
                <Settings size={20} className="text-white" />
              </div>
              <h2 className="text-xl font-semibold text-text-primary">Event Retention</h2>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Event Retention Days
              </label>
              <input
                type="number"
                min="1"
                max="365"
                value={config.event_retention_days}
                onChange={(e) => handleInputChange('event_retention_days', e.target.value)}
                className="w-full px-4 py-2 bg-surface-secondary border border-surface-secondary rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
              />
              <p className="text-xs text-text-muted mt-2">
                Events older than this many days will be automatically deleted. Range: 1-365 days. 
                Recommended: 30-60 days for most applications.
              </p>
              <div className="mt-3 p-3 bg-surface-secondary rounded-lg border border-surface-tertiary">
                <p className="text-xs text-text-muted">
                  <strong>Note:</strong> Automatic cleanup runs daily. Events are permanently deleted and cannot be recovered.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ConfigurationPage;
